import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Trophy, Crown, Medal, Sparkles, TrendingUp, Film,
  IndianRupee, Loader2, ArrowLeft, Instagram
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SCOPES = [
  { key: "month", label: "This month" },
  { key: "rolling_30", label: "Last 30d" },
  { key: "season", label: "This season" },
  { key: "all_time", label: "All time" },
];

const SORTS = [
  { key: "hybrid", label: "Overall", icon: Trophy },
  { key: "bookings", label: "Bookings", icon: Sparkles },
  { key: "revenue", label: "Revenue", icon: IndianRupee },
  { key: "content", label: "Content", icon: Film },
];

const TIER_META = {
  bronze: { label: "Bronze", color: "from-amber-600 to-amber-800", emoji: "🥉" },
  silver: { label: "Silver", color: "from-slate-400 to-slate-600", emoji: "🥈" },
  gold: { label: "Gold", color: "from-yellow-400 to-yellow-600", emoji: "🥇" },
  platinum: { label: "Platinum", color: "from-indigo-400 to-pink-500", emoji: "💎" },
};

const fmtINR = (n) => `₹${Math.round(n || 0).toLocaleString("en-IN")}`;

const Podium = ({ rows }) => {
  if (rows.length === 0) return null;
  // Re-order for visual podium: [2nd, 1st, 3rd]
  const order = [rows[1], rows[0], rows[2]].filter(Boolean);
  const heights = { 0: "h-36", 1: "h-48", 2: "h-28" };
  const medals = { 0: <Medal className="text-slate-300" />, 1: <Crown className="text-yellow-400" size={24} />, 2: <Medal className="text-amber-700" /> };

  return (
    <div className="flex items-end justify-center gap-2 sm:gap-4 mb-8" data-testid="creator-podium">
      {order.map((r, idx) => {
        const tier = TIER_META[r.tier] || null;
        return (
          <div key={r.creator_id} className="flex-1 max-w-[180px] flex flex-col items-center">
            <div className="mb-1">{medals[idx]}</div>
            <div className="w-full bg-white rounded-t-2xl p-3 shadow-lg border border-gray-100 flex flex-col items-center">
              <div className={`w-12 h-12 rounded-full bg-gradient-to-br ${idx === 1 ? "from-[#E83E8C] to-[#FF9F1C]" : "from-indigo-400 to-purple-500"} flex items-center justify-center text-white font-bold text-lg mb-1`}>
                {(r.name || "C").slice(0, 1).toUpperCase()}
              </div>
              <p className="font-bold text-[11px] text-[#1E1B4B] text-center truncate w-full" data-testid={`podium-name-${r.rank}`}>{r.name}</p>
              {tier && (
                <span className={`text-[9px] mt-0.5 px-1.5 py-0.5 rounded-full text-white bg-gradient-to-r ${tier.color}`}>
                  {tier.emoji} {tier.label}
                </span>
              )}
              <p className="text-[10px] text-gray-500 mt-1">{r.bookings} bookings</p>
              <p className="text-[10px] font-mono font-bold text-[#E83E8C]">{r.score} pts</p>
            </div>
            <div className={`w-full bg-gradient-to-t ${idx === 1 ? "from-yellow-400 to-yellow-200" : idx === 0 ? "from-slate-400 to-slate-200" : "from-amber-600 to-amber-400"} ${heights[idx]} rounded-b-xl flex items-start justify-center pt-2`}>
              <span className="text-2xl font-black text-white drop-shadow-md">#{r.rank}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Row = ({ r }) => {
  const tier = TIER_META[r.tier] || null;
  return (
    <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-gray-100 hover:border-[#E83E8C]/40 transition" data-testid={`creator-row-${r.rank}`}>
      <span className={`w-8 text-center font-black ${r.rank === 1 ? "text-yellow-500" : r.rank === 2 ? "text-slate-400" : r.rank === 3 ? "text-amber-700" : "text-gray-400"}`}>
        #{r.rank}
      </span>
      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-400 to-pink-500 flex items-center justify-center text-white font-bold">
        {(r.name || "C").slice(0, 1).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <p className="font-bold text-[#1E1B4B] text-sm truncate">{r.name}</p>
          {tier && (
            <span className={`text-[9px] px-1.5 py-0.5 rounded-full text-white bg-gradient-to-r ${tier.color}`}>
              {tier.emoji}
            </span>
          )}
        </div>
        {r.instagram && (
          <a href={`https://instagram.com/${(r.instagram || "").replace("@", "")}`} target="_blank" rel="noreferrer" className="text-[10px] text-gray-500 flex items-center gap-0.5 hover:text-pink-500">
            <Instagram size={9} /> @{(r.instagram || "").replace("@", "")}
          </a>
        )}
      </div>
      <div className="text-right">
        <p className="text-[10px] uppercase text-gray-400">Score</p>
        <p className="font-extrabold text-[#E83E8C]">{r.score}</p>
      </div>
      <div className="hidden sm:block text-right text-[11px] text-gray-500 min-w-[80px]">
        <p>{r.bookings} bkgs</p>
        <p>{fmtINR(r.revenue)}</p>
        <p>{r.verified_uploads} posts</p>
      </div>
    </div>
  );
};

export default function CreatorLeaderboard() {
  const [scope, setScope] = useState("month");
  const [sortBy, setSortBy] = useState("hybrid");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/creators/leaderboard`, {
        params: { scope, sort_by: sortBy, limit: 50 },
      });
      setRows(r.data.rows || []);
    } catch { setRows([]); }
    setLoading(false);
  }, [scope, sortBy]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFF0F5] via-white to-[#FFF7ED]" data-testid="creator-leaderboard-page">
      <div className="max-w-3xl mx-auto px-4 py-6">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-[#E83E8C] mb-4" data-testid="back-to-home">
          <ArrowLeft size={14} /> Back
        </Link>

        <header className="text-center mb-6">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#E83E8C] to-[#FF9F1C] text-white px-4 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider mb-3">
            <Trophy size={14} /> Creator Leaderboard
          </div>
          <h1 className="text-3xl sm:text-4xl font-black text-[#1E1B4B]">Who's throwing the biggest parties?</h1>
          <p className="text-sm text-gray-600 mt-2">
            Top DoozyGo Creators ranked by bookings, revenue, and viral content.
            <Link to="/creators/dashboard" className="text-[#E83E8C] font-bold ml-1 hover:underline" data-testid="creator-join-cta">Become a Creator →</Link>
          </p>
        </header>

        {/* Scope tabs */}
        <div className="flex items-center gap-1 bg-white rounded-2xl p-1 mb-3 overflow-x-auto" data-testid="scope-tabs">
          {SCOPES.map((s) => (
            <button
              key={s.key}
              onClick={() => setScope(s.key)}
              className={`flex-1 min-w-[88px] px-3 py-2 rounded-xl text-xs font-bold transition ${scope === s.key ? "bg-[#1E1B4B] text-white" : "text-gray-600 hover:bg-gray-50"}`}
              data-testid={`scope-${s.key}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {/* Sort tabs */}
        <div className="flex items-center gap-1 mb-5 overflow-x-auto">
          {SORTS.map((s) => {
            const Icon = s.icon;
            return (
              <button
                key={s.key}
                onClick={() => setSortBy(s.key)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-bold transition ${sortBy === s.key ? "bg-[#E83E8C] text-white" : "bg-white text-gray-600 border border-gray-200 hover:border-[#E83E8C]"}`}
                data-testid={`sort-${s.key}`}
              >
                <Icon size={12} /> {s.label}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-[#E83E8C]" /></div>
        ) : rows.length === 0 ? (
          <div className="bg-white rounded-2xl p-8 text-center border border-gray-100">
            <TrendingUp size={32} className="mx-auto text-gray-300 mb-2" />
            <p className="text-sm text-gray-600">No creators ranked yet for this window.</p>
            <Link to="/creators/dashboard" className="inline-block mt-3 text-[#E83E8C] font-bold text-xs hover:underline">Be the first →</Link>
          </div>
        ) : (
          <>
            <Podium rows={rows.slice(0, 3)} />
            <div className="space-y-2" data-testid="creator-full-list">
              {rows.slice(3).map((r) => <Row key={r.creator_id} r={r} />)}
            </div>
          </>
        )}

        {/* Legend */}
        <div className="mt-8 bg-white rounded-2xl p-4 border border-gray-100 text-xs text-gray-600">
          <p className="font-bold text-[#1E1B4B] mb-2">How scoring works</p>
          <p className="leading-relaxed">
            <b>Hybrid score</b> = bookings × 60 + (revenue in lakhs) × 30 + verified uploads × 10.
            Tiers are earned by all-time attributed bookings:
            {" "}<span className="text-amber-700 font-bold">Bronze (5)</span> ·
            {" "}<span className="text-slate-500 font-bold">Silver (15)</span> ·
            {" "}<span className="text-yellow-600 font-bold">Gold (50)</span> ·
            {" "}<span className="text-pink-500 font-bold">Platinum (100)</span>.
          </p>
          <p className="mt-2 leading-relaxed">
            Top 3 each month earn Razorpay payouts (₹5k · ₹3k · ₹2k).
            Every attributed booking also earns you ₹500 in party credits.
          </p>
        </div>
      </div>
    </div>
  );
}
